import type { InferType, Schema } from '@faceup/validation'
import { useLocalStorage } from '@mantine/hooks'
import merge from 'deepmerge'
import { useCallback } from 'react'

const initializedStorages: Record<string, boolean> = {}

type InvalidationFix = 'resetToDefault' | 'tryToPreserve'

export type LocalStorageOptions = {
  skipSingleInitializationLimit: boolean
  invalidDataFix: InvalidationFix
  /**
   * Read the stored value during the first render instead of in a mount effect. Opt in wherever the
   * default value would otherwise flash on screen before the stored one replaces it.
   */
  readStoredValueOnFirstRender: boolean
}

const defaultOptions: LocalStorageOptions = {
  skipSingleInitializationLimit: false,
  invalidDataFix: 'tryToPreserve',
  readStoredValueOnFirstRender: false,
}

// biome-ignore lint/suspicious/noExplicitAny: Yup schema generic requires any type parameter
export const createLocalStorage = <S extends Schema<any>>(
  key: string,
  schema: S,
  initialValue: InferType<S>,
  options?: Partial<LocalStorageOptions>
): (() => [InferType<S>, (value: InferType<S>) => void]) => {
  const opts = { ...defaultOptions, ...options }
  try {
    schema.validateSync(initialValue, { abortEarly: false })
    // Validation passed
  } catch (error) {
    throw new Error(
      `Initial value for local storage with key "${key}" does not match schema: ${error}`
    )
  }
  if (initializedStorages[key] && !opts.skipSingleInitializationLimit) {
    console.error(`Local storage with key "${key}" is already initialized.`)
  }
  initializedStorages[key] = true

  const validateValue: (value: InferType<S>) => InferType<S> = (value: InferType<S>) => {
    try {
      schema.validateSync(value, { abortEarly: false })
      return value
    } catch (error) {
      const handleInvalidDataFix: Record<InvalidationFix, () => InferType<S>> = {
        resetToDefault: () => {
          console.error(`Value for local storage with key "${key}" does not match schema: ${error}`)
          return initialValue
        },
        tryToPreserve: () => {
          console.error(
            `Value for local storage with key "${key}" does not match schema: ${error} Trying to fix it...`
          )
          try {
            const newValue = merge(initialValue, value)
            schema.validateSync(newValue, { abortEarly: false })
            return newValue
          } catch {
            console.error('Fix unsuccessful, returning initial value')
            return initialValue
          }
        },
      }

      return handleInvalidDataFix[opts.invalidDataFix]()
    }
  }

  return () => {
    const [storedValue, setStoredValue] = useLocalStorage<InferType<S>>({
      key,
      defaultValue: initialValue,
      getInitialValueInEffect: !opts.readStoredValueOnFirstRender,
    })

    return [
      validateValue(storedValue),
      // biome-ignore lint/correctness/useExhaustiveDependencies: SetStoredValue is stable, adding it causes infinite validation loops
      useCallback(value => setStoredValue(validateValue(value)), []),
    ]
  }
}
